import * as React from "react";
import { useLocalization } from "@shared-ui/localization-context";

import { UitkHeading } from "uitk-react-text";
import { AnalyticsConfig } from "uitk-react-utils";
import { UitkSpacing } from "uitk-react-spacing";

import { ProductTypeContext, ProductTypeDescriptionContext } from "components/flexComponents/TopCities/TopCities";
import { cityCard, topLevelComponentClassName, carouselItemsVisible } from "./topCitiesDestinationUtils";
import { ItemKeyHelper } from "components/utility/ItemKeyHelper";
import { Action, FlexTrackingInfo, sendDelayedTrackEvent } from "components/utility/analytics/FlexAnalyticsUtils";

import { TopCitiesProps } from "components/flexComponents/TopCities/typings";
import { withStores } from "stores";
import { IsomorphicCarousel } from "src/components/shared/IsomorphicCarousel/IsomorphicCarousel";
import { cropResizeSrc } from "src/components/shared/BlossomImage/BlossomImage";
import { observer } from "mobx-react";
import { TopCitiesFlexModuleDestinationResult } from "typings/microserviceModels/top-cities-flex-module";

const TopCitiesDestination = withStores(
  "analytics",
  "flexModuleModelStore",
  "context"
)(
  observer((props: TopCitiesProps) => {
    const { templateComponent, flexModuleModelStore, context, analytics } = props;
    const {
      metadata: { id },
      config: { view = "" },
    } = templateComponent;
    const model = flexModuleModelStore.getModel(id) as TopCitiesFlexModuleDestinationResult;
    const { formatText } = useLocalization();
    const cityCardKeyHelper = new ItemKeyHelper("topCitiesCard");

    if (!model || !model.topCities || model.empty) {
      return null;
    }

    const { title, topCities, productType, productTypeDescription, hideProductIcon } = model;

    /* istanbul ignore next */
    const carouselAnalytics: AnalyticsConfig = {
      id: `LP.TopCities.${productType}`,
      callback: (theId, description) => {
        const flexTrackingInfo: FlexTrackingInfo = {
          moduleName: theId,
          action: Action.SCROLL,
          linkName: description,
        };
        sendDelayedTrackEvent(flexTrackingInfo, analytics);
      },
    };

    const CityCard = cityCard[view];

    const getHeadingText = () => {
      if (title) {
        return title;
      }
      const destination = context.searchContext.location?.localizedName || context.searchContext.destination.name;

      return formatText("topCities.title", destination);
    };

    const topCitiesWithOptimizedImage = topCities.map((city) => {
      const images = city.images.map((img) => ({
        ...img,
        url: cropResizeSrc(img.url, { width: 384, height: 216 }, "medium"),
      }));

      return {
        ...city,
        images,
      };
    });

    return (
      <ProductTypeContext.Provider value={productType.toLowerCase()}>
        <ProductTypeDescriptionContext.Provider value={productTypeDescription}>
          <div id={id} className={topLevelComponentClassName[view]}>
            <UitkHeading tag="h2" size={4}>
              {getHeadingText()}
            </UitkHeading>

            <UitkSpacing margin={{ blockstart: "three" }}>
              <div>
                <IsomorphicCarousel
                  buttonText={{
                    nextButton: formatText("carousel.item.next"),
                    prevButton: formatText("carousel.item.prev"),
                  }}
                  itemsVisible={carouselItemsVisible[view]}
                  pageBy={1}
                  peek
                  carouselName="topCitiesCarousel"
                  analytics={carouselAnalytics}
                >
                  {topCitiesWithOptimizedImage.map((city, cityIndex) => (
                    <CityCard
                      hideProductIcon={hideProductIcon}
                      key={cityCardKeyHelper.next()}
                      city={city}
                      index={cityIndex}
                      pageType={context.searchContext.pageType}
                    />
                  ))}
                </IsomorphicCarousel>
              </div>
            </UitkSpacing>
          </div>
        </ProductTypeDescriptionContext.Provider>
      </ProductTypeContext.Provider>
    );
  })
);

export default TopCitiesDestination;
